import React, { type FC, type ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import { reportError } from "@app/services/error-reporter";
import { attachKeyboardScanner } from "./keyboardScannerAdapter";
import {
    ScannerContext,
    type ScannerHintUiStateUpdate,
    type ScannerRegistrationHandle,
    type ScannerRegistryState,
} from "./ScannerContext";
import { ScannerHintOverlay } from "./ScannerHintOverlay";
import type {
    ScannerHandlerEntry,
    ScannerHandlerPriority,
    ScannerHintConfig,
    ScannerHintUiState,
    ScannerRegistrationConfig,
    ScannerScanTarget,
    ScannerStatus,
} from "./types";

/** Пропсы компонента {@link ScannerProvider}. */
interface ScannerProviderProps {
    /** Дочернее дерево приложения. */
    children: ReactNode;
}

type ScannerRegistryAction =
    | { type: "register"; entry: ScannerHandlerEntry }
    | { type: "update"; id: string; config: ScannerRegistrationConfig }
    | { type: "unregister"; id: string }
    | { type: "busy"; busy: boolean };

const SCANNER_PRIORITY_WEIGHT: Record<ScannerHandlerPriority, number> = {
    screen: 10,
    window: 20,
    dialog: 30,
    critical: 40,
};

const initialState: ScannerRegistryState = {
    entries: [],
    busy: false,
};

const EMPTY_HINT_OFFSET = { x: 0, y: 0 };

function buildDefaultScannerHintUiState(hint: ScannerHintConfig): ScannerHintUiState {
    return {
        collapsed: hint.initialCollapsed ?? false,
        dragOffset: EMPTY_HINT_OFFSET,
        dismissed: false,
    };
}

function scannerRegistryReducer(state: ScannerRegistryState, action: ScannerRegistryAction): ScannerRegistryState {
    if (action.type === "register") {
        return {
            ...state,
            entries: [...state.entries, action.entry],
        };
    }

    if (action.type === "update") {
        let hasChanges = false;
        const entries = state.entries.map((entry) => {
            if (entry.id !== action.id) {
                return entry;
            }

            const updatedEntry = toUpdatedEntry(entry, action.config);
            hasChanges = hasChanges || !scannerEntriesEqual(entry, updatedEntry);
            return updatedEntry;
        });

        if (!hasChanges) {
            return state;
        }

        return {
            ...state,
            entries,
        };
    }

    if (action.type === "unregister") {
        return {
            ...state,
            entries: state.entries.filter((entry) => entry.id !== action.id),
        };
    }

    return {
        ...state,
        busy: action.busy,
    };
}

function toUpdatedEntry(entry: ScannerHandlerEntry, config: ScannerRegistrationConfig): ScannerHandlerEntry {
    return {
        ...entry,
        enabled: config.enabled ?? true,
        paused: config.paused ?? false,
        priority: config.priority ?? "screen",
        hint: config.hint,
        captureInEditable: config.captureInEditable ?? false,
        onScan: config.onScan,
        errorSource: config.errorSource,
    };
}

function scannerEntriesEqual(left: ScannerHandlerEntry, right: ScannerHandlerEntry): boolean {
    return (
        left.enabled === right.enabled &&
        left.paused === right.paused &&
        left.priority === right.priority &&
        left.hint === right.hint &&
        left.captureInEditable === right.captureInEditable &&
        left.onScan === right.onScan &&
        left.errorSource === right.errorSource
    );
}

function compareScannerEntries(left: ScannerHandlerEntry, right: ScannerHandlerEntry): number {
    const priorityDiff = SCANNER_PRIORITY_WEIGHT[left.priority] - SCANNER_PRIORITY_WEIGHT[right.priority];

    if (priorityDiff !== 0) {
        return priorityDiff;
    }

    return left.order - right.order;
}

function resolveActiveEntry(entries: ScannerHandlerEntry[]): ScannerHandlerEntry | undefined {
    return entries
        .filter((entry) => entry.enabled)
        .sort(compareScannerEntries)
        .at(-1);
}

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    if (target.isContentEditable) {
        return true;
    }

    return (
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement
    );
}

function resolveHintStateKey(activeEntry: ScannerHandlerEntry | undefined): string | undefined {
    if (activeEntry?.hint == null) {
        return undefined;
    }

    return activeEntry.hint.stateKey ?? activeEntry.id;
}

function buildScannerStatus(activeEntry: ScannerHandlerEntry | undefined, scanBusy: boolean): ScannerStatus {
    return {
        active: activeEntry != null,
        busy: scanBusy || activeEntry?.paused === true,
        hint: activeEntry?.hint,
        hintStateKey: resolveHintStateKey(activeEntry),
    };
}

/**
 * App-level provider аппаратного сканирования.
 *
 * Держит единственную подписку на клавиатурный ввод ТСД и маршрутизирует сканы
 * в topmost обработчик, зарегистрированный текущим экраном или overlay.
 *
 * ## Порядок с `OverlayProvider`
 *
 * `ScannerProvider` должен быть **снаружи** `OverlayProvider` (см. `AppProviders`).
 *
 * React Context идёт по дереву компонентов, а не по DOM. `OverlayHost` рендерит окна
 * через portal, но в React-дереве они остаются потомками `OverlayProvider`.
 * Если поставить `ScannerProvider` внутрь `OverlayProvider`, `OverlayHost` окажется
 * **соседом** `ScannerProvider`, и window-content (`useScannerRegistration` с
 * `priority: "window"`) потеряет `ScannerContext` — краш при открытии форм в overlay.
 *
 * `ScannerOverlaySuppressBridge` монтируется в `AppProviders` **внутри обоих**
 * провайдеров: ему нужен overlay stack и setter глобальной паузы из этого context.
 *
 * ## Z-index подсказки
 *
 * Порядок провайдеров **не** решает stacking. `ScannerHintOverlay` порталится в
 * `document.body` и использует `--z-scannerHint` из темы.
 */
export const ScannerProvider: FC<ScannerProviderProps> = ({ children }) => {
    const [state, dispatch] = React.useReducer(scannerRegistryReducer, initialState);
    const [globalSuppress, setGlobalSuppress] = React.useState(false);
    const [hintStates, setHintStates] = React.useState<Record<string, ScannerHintUiState>>({});
    const stateRef = React.useRef(state);
    const globalSuppressRef = React.useRef(globalSuppress);
    const nextOrderRef = React.useRef(0);

    stateRef.current = state;
    globalSuppressRef.current = globalSuppress;

    const activeEntry = React.useMemo(() => {
        if (globalSuppress) {
            return undefined;
        }

        return resolveActiveEntry(state.entries);
    }, [globalSuppress, state.entries]);
    const activeEntryRef = React.useRef(activeEntry);
    activeEntryRef.current = activeEntry;
    const activeHintStateKey = resolveHintStateKey(activeEntry);
    const activeHintStateKeyRef = React.useRef(activeHintStateKey);
    activeHintStateKeyRef.current = activeHintStateKey;

    const status = React.useMemo(() => buildScannerStatus(activeEntry, state.busy), [activeEntry, state.busy]);
    const hintUiState = React.useMemo(() => {
        const hint = activeEntry?.hint;
        if (hint == null || activeHintStateKey == null) {
            return undefined;
        }

        return hintStates[activeHintStateKey] ?? buildDefaultScannerHintUiState(hint);
    }, [activeEntry?.hint, activeHintStateKey, hintStates]);

    const register = React.useCallback((config: ScannerRegistrationConfig): ScannerRegistrationHandle => {
        const id = uuidv4();
        const entry: ScannerHandlerEntry = {
            id,
            order: nextOrderRef.current,
            enabled: config.enabled ?? true,
            paused: config.paused ?? false,
            priority: config.priority ?? "screen",
            hint: config.hint,
            captureInEditable: config.captureInEditable ?? false,
            onScan: config.onScan,
            errorSource: config.errorSource,
        };

        nextOrderRef.current += 1;
        dispatch({ type: "register", entry });

        return {
            id,
            unregister: () => {
                dispatch({ type: "unregister", id });
            },
        };
    }, []);

    const update = React.useCallback((id: string, config: ScannerRegistrationConfig) => {
        dispatch({ type: "update", id, config });
    }, []);

    const updateHintUiState = React.useCallback(
        (hintStateKey: string, defaultState: ScannerHintUiState, update: ScannerHintUiStateUpdate) => {
            setHintStates((currentHintStates) => {
                const currentState = currentHintStates[hintStateKey] ?? defaultState;
                const nextState = typeof update === "function" ? update(currentState) : update;

                return {
                    ...currentHintStates,
                    [hintStateKey]: nextState,
                };
            });
        },
        [],
    );

    const updateActiveHintUiState = React.useCallback(
        (update: ScannerHintUiStateUpdate) => {
            const hintStateKey = activeHintStateKeyRef.current;
            const hint = activeEntryRef.current?.hint;
            if (hintStateKey == null || hint == null) {
                return;
            }

            updateHintUiState(hintStateKey, buildDefaultScannerHintUiState(hint), update);
        },
        [updateHintUiState],
    );

    const dispatchScanToEntry = React.useCallback(
        (entry: ScannerHandlerEntry, barcode: string, options?: { ignorePaused?: boolean }): boolean => {
            if (entry.paused && options?.ignorePaused !== true) {
                return false;
            }

            if (stateRef.current.busy) {
                return false;
            }

            dispatch({ type: "busy", busy: true });

            try {
                Promise.resolve(entry.onScan(barcode))
                    .catch((error) => {
                        reportError(error, { from: entry.errorSource ?? "scanner.onScan" });
                    })
                    .finally(() => {
                        dispatch({ type: "busy", busy: false });
                    });
            } catch (error) {
                dispatch({ type: "busy", busy: false });
                reportError(error, { from: entry.errorSource ?? "scanner.onScan" });
                return false;
            }

            return true;
        },
        [],
    );

    const handleScan = React.useCallback(
        (barcode: string): void => {
            if (globalSuppressRef.current) {
                return;
            }

            const entry = activeEntryRef.current;
            if (entry == null) {
                return;
            }

            dispatchScanToEntry(entry, barcode);
        },
        [dispatchScanToEntry],
    );

    const captureActiveScanTarget = React.useCallback((): ScannerScanTarget | null => {
        const entry = activeEntryRef.current;
        if (entry == null) {
            return null;
        }

        const registrationId = entry.id;

        return {
            registrationId,
            inject: (barcode: string): boolean => {
                const currentEntry = stateRef.current.entries.find((item) => item.id === registrationId);
                if (currentEntry == null || !currentEntry.enabled) {
                    return false;
                }

                return dispatchScanToEntry(currentEntry, barcode, { ignorePaused: true });
            },
        };
    }, [dispatchScanToEntry]);

    React.useEffect(() => {
        return attachKeyboardScanner({
            canCapture: (event) => {
                if (globalSuppressRef.current) {
                    return false;
                }

                const entry = activeEntryRef.current;
                if (entry == null || entry.paused || stateRef.current.busy) {
                    return false;
                }

                return entry.captureInEditable || !isEditableTarget(event.target);
            },
            onScan: handleScan,
        });
    }, [handleScan]);

    const contextValue = React.useMemo(
        () => ({
            status,
            hintUiState,
            register,
            update,
            updateActiveHintUiState,
            updateHintUiState,
            setGlobalSuppress,
            captureActiveScanTarget,
        }),
        [
            captureActiveScanTarget,
            hintUiState,
            register,
            setGlobalSuppress,
            status,
            update,
            updateActiveHintUiState,
            updateHintUiState,
        ],
    );

    return (
        <ScannerContext.Provider value={contextValue}>
            {children}
            <ScannerHintOverlay />
        </ScannerContext.Provider>
    );
};
